import { Download, FileText } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { QuizQuestion } from '@/types/quizzes'
import { QuizAttemptFile } from '@/types/quizzes'

interface QuizAnswerPreviewProps {
  question?: QuizQuestion
  answer: any
  files?: QuizAttemptFile[] | null
}

export function QuizAnswerPreview({ question, answer, files }: QuizAnswerPreviewProps) {
  if (!question) {
    return <p className="text-sm text-gray-500">Question metadata not available.</p>
  }

  switch (question.type) {
    case 'mcq':
    case 'multi': {
      const selected = Array.isArray(answer?.selected) ? answer.selected : []
      return (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Selected answers</p>
          <div className="flex flex-wrap gap-2">
            {selected.length === 0 ? (
              <Badge variant="outline">No answer selected</Badge>
            ) : (
              selected.map((optionKey: string) => (
                <Badge key={`${question.id}-${optionKey}`} variant="secondary">
                  {optionKey}
                </Badge>
              ))
            )}
          </div>
        </div>
      )
    }
    case 'numeric':
      return (
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          Answer provided: <strong>{answer?.value ?? 'No answer'}</strong>
        </div>
      )
    case 'short_answer':
      return (
        <div className="rounded-lg border border-purple-100 bg-purple-50 px-4 py-3 text-sm text-purple-700">
          {answer?.text ?? 'No answer submitted.'}
        </div>
      )
    case 'file_upload': {
      const relatedFiles = files ?? []
      const matches = relatedFiles.filter((file) =>
        (answer?.files ?? []).some(
          (answerFile: any) =>
            answerFile.filename === file.fileName,
        ),
      )

      return (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">Uploaded files</p>
          {matches.length > 0 ? (
            <ul className="space-y-2">
              {matches.map((file) => (
                <li
                  key={file.id}
                  className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2 text-sm"
                >
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4 text-gray-400" />
                    {file.fileName}
                  </span>
                  {file.url && (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <Download className="h-4 w-4" />
                      Download
                    </a>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No files were uploaded.</p>
          )}
        </div>
      )
    }
    default:
      return null
  }
}
